import type { SalaryPreset } from '../../types/tax';
import { formatCurrency } from '../../utils/formatting';

interface PresetButtonsProps {
  onPresetSelect: (monthlyAmount: number) => void;
  disabled?: boolean;
  currentFrequency: 'monthly' | 'yearly';
}

// Common South African salary ranges
const SALARY_PRESETS: SalaryPreset[] = [
  {
    label: 'Entry Level',
    monthlyAmount: 15000,
    description: 'Typical entry-level professional salary',
  },
  {
    label: 'Mid Level',
    monthlyAmount: 25000,
    description: 'Mid-career professional salary',
  },
  {
    label: 'Senior Level',
    monthlyAmount: 40000,
    description: 'Senior professional salary',
  },
  {
    label: 'Executive',
    monthlyAmount: 60000,
    description: 'Executive/management level salary',
  },
];

export default function PresetButtons({ onPresetSelect, disabled = false, currentFrequency }: PresetButtonsProps) {
  const handlePresetClick = (monthlyAmount: number) => {
    if (disabled) return;
    onPresetSelect(monthlyAmount);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-gray-700 dark:text-slate-300">Quick Presets</h4>
        <span className="text-xs text-gray-500 dark:text-slate-500">
          Common SA salary ranges
        </span>
      </div>
      
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {SALARY_PRESETS.map((preset) => {
          const displayAmount = currentFrequency === 'yearly' 
            ? preset.monthlyAmount * 12 
            : preset.monthlyAmount;
          
          return (
            <button
              key={preset.label}
              type="button"
              onClick={() => handlePresetClick(preset.monthlyAmount)}
              disabled={disabled}
              className={`
                px-3 py-2 text-xs font-medium rounded-lg border text-center transition-all duration-200
                ${disabled 
                  ? 'bg-gray-100 dark:bg-slate-800 text-gray-400 dark:text-slate-500 border-gray-200 dark:border-slate-700 cursor-not-allowed' 
                  : 'bg-white dark:bg-slate-800 text-charcoal dark:text-slate-100 border-gray-200 dark:border-slate-600 hover:bg-gray-50 dark:hover:bg-slate-700 hover:border-sa-green dark:hover:border-sa-green focus:outline-none focus:ring-2 focus:ring-sa-green focus:border-sa-green'
                }
              `}
              title={preset.description}
            >
              <div className="space-y-1">
                <div className="font-semibold">{preset.label}</div>
                <div className="text-gray-600 dark:text-slate-300">
                  {formatCurrency(displayAmount)}
                </div>
                <div className="text-gray-500 dark:text-slate-500">
                  per {currentFrequency === 'yearly' ? 'year' : 'month'}
                </div>
              </div>
            </button>
          );
        })}
      </div>
      
      <p className="text-xs text-gray-500 dark:text-slate-500">
        Click a preset to quickly populate the income field with common SA salary amounts
      </p>
    </div>
  );
}